import { Fragment, useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { mascaraDaRolagem, useRolagemHorizontal } from '../core/rolagem';
import { SetaDeRolagem } from '../core/SetaDeRolagem';

export interface KanbanItem {
    id?: string | number;
    [campo: string]: unknown;
}

export interface KanbanColumn<Item extends KanbanItem = KanbanItem> {
    id: string;
    title: ReactNode;
    items: Item[];
    color?: string;
    /** Total da coluna, já formatado. */
    total?: ReactNode;
}

export interface KanbanBoardProps<Item extends KanbanItem = KanbanItem> {
    columns?: KanbanColumn<Item>[];
    renderCard: (item: Item, coluna: KanbanColumn<Item>) => ReactNode;
    style?: CSSProperties;
}

export function KanbanBoard<Item extends KanbanItem = KanbanItem>({ columns = [], renderCard, style }: KanbanBoardProps<Item>) {
    const quadro = useRef<HTMLDivElement | null>(null);
    const rolagem = useRolagemHorizontal(quadro, columns.length);

    /* O quadro rola de lado DENTRO de si: a página não rola, e a coluna que não
       coube ("Ganho", a que mais importa) é anunciada pela borda esmaecida e,
       com mouse, pela seta. O respiro depois da última coluna existe para ela
       não terminar colada na borda quando se rola até o fim. */
    return (
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', minWidth: 0, maxWidth: '100%', ...style }}>
            <div
                ref={quadro} data-rolagem="horizontal"
                style={{ flex: 1, minHeight: 0, display: 'flex', gap: 14, alignItems: 'flex-start', overflowX: 'auto', paddingBottom: 6, paddingRight: 16, ...mascaraDaRolagem(rolagem, 40) }}
            >
                {columns.map((col) => (
                    <section
                        key={col.id}
                        aria-label={typeof col.title === 'string' ? col.title : undefined}
                        style={{ width: 272, flex: '0 0 auto', background: 'var(--surface-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', display: 'flex', flexDirection: 'column', maxHeight: '100%', overflow: 'hidden' }}
                    >
                        {col.color && <span aria-hidden="true" style={{ display: 'block', height: 3, background: col.color }} />}
                        <header style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 12px 9px' }}>
                            {col.color && <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: col.color, flex: '0 0 auto' }} />}
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{col.title}</span>
                            <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', padding: '0 7px' }}>
                                {col.items.length}
                            </span>
                            {col.total && <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{col.total}</span>}
                        </header>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 8px 10px', overflowY: 'auto' }}>
                            {col.items.map((item, i) => (
                                <Fragment key={item.id ?? i}>{renderCard(item, col)}</Fragment>
                            ))}
                            {col.items.length === 0 && (
                                <p style={{ margin: 0, padding: '18px 8px', textAlign: 'center', fontSize: 12.5, color: 'var(--text-tertiary)' }}>Sem oportunidades</p>
                            )}
                        </div>
                    </section>
                ))}
            </div>
            {rolagem.ponteiroFino && rolagem.antes && <SetaDeRolagem lado="antes" topo={22} onClick={() => rolagem.rolar(-1)} />}
            {rolagem.ponteiroFino && rolagem.depois && <SetaDeRolagem lado="depois" topo={22} onClick={() => rolagem.rolar(1)} />}
        </div>
    );
}
